import { OrderedItemStatus } from './OrderedItemStatus.model';
import { TestCodes } from './TestCodes.model';

export type ResultSection = 'unread' | 'read';

export class ReportHeader {
  readonly categories = ['new', 'upcoming', 'updated', 'recently_viewed'];
  id!: string;
  dateOfService!: string;
  reportDate!: string;
  orderingProviderName!: string;
  accountName!: string;
  isDetailAvailable!: boolean;
  hasMinimumDataForPdfRetrieval!: boolean;
  dashboardCategory!: string;
  lastViewedDate?: string;
  weHealthEligible!: boolean;
  patientName!: string;
  pid!: number;
  orderedDate!: string;
  testCodes!: TestCodes[];
  totalTests?: number;
  completedTests?: number;
  itemStatuses?: Record<string, OrderedItemStatus>;
  private readonly _itemStatusList: OrderedItemStatus[];
  constructor(reportHeader: ReportHeader) {
    if (reportHeader) {
      this.id = reportHeader.id;
      this.dateOfService = reportHeader.dateOfService;
      this.reportDate = reportHeader.reportDate;
      this.orderingProviderName = reportHeader.orderingProviderName;
      this.accountName = reportHeader.accountName;
      this.isDetailAvailable = reportHeader.isDetailAvailable;
      this.hasMinimumDataForPdfRetrieval =
        reportHeader.hasMinimumDataForPdfRetrieval;
      this.dashboardCategory = reportHeader.dashboardCategory;
      this.lastViewedDate = reportHeader.lastViewedDate;
      this.weHealthEligible = reportHeader.weHealthEligible;
      this.patientName = reportHeader.patientName;
      this.pid = reportHeader.pid;
      this.orderedDate = reportHeader.orderedDate;
      this.testCodes = reportHeader.testCodes;
      this.totalTests = reportHeader.totalTests;
      this.completedTests = reportHeader.completedTests;
      this.itemStatuses = reportHeader.itemStatuses;
    }
    // Computed eagerly (rather than lazily cached on first getter access)
    // because instances of this class end up frozen once they reach the
    // NgRx store, which would make a lazy assignment throw.
    this._itemStatusList = this.itemStatuses
      ? Object.values(this.itemStatuses)
      : [];
  }

  get normalizedDashboardCategory(): string {
    return this.dashboardCategory.toLocaleLowerCase();
  }

  get linkEnabled(): boolean {
    return this.normalizedDashboardCategory !== this.categories[1];
  }

  get finalReportDateHeader(): string {
    switch (this.normalizedDashboardCategory) {
      case this.categories[0]:
      case this.categories[3]: {
        return 'Final Report Date';
      }

      case this.categories[1]: {
        return 'Report Date';
      }

      case this.categories[2]: {
        return 'Updated Final Report Date';
      }

      default: {
        return '';
      }
    }
  }

  get formattedDashboardCategory(): string {
    return this.dashboardCategory === this.categories[3]
      ? ''
      : this.dashboardCategory === this.categories[0]
      ? 'unread'
      : this.dashboardCategory;
  }

  get isComplete(): boolean {
    if (!this.itemStatusList.length) {
      return this.dashboardCategory !== 'upcoming';
    }
    return !this.itemStatusList.some(
      (item) => item.status === 'UPCOMING' || item.status === 'PRELIMINARY'
    );
  }

  get accessionStatus(): 'complete' | 'in-progress' {
    return this.isComplete ? 'complete' : 'in-progress';
  }

  get isUnread(): boolean {
    if (this.dashboardCategory === 'upcoming') {
      return !this.lastViewedDate;
    }
    return (
      this.dashboardCategory === 'new' || this.dashboardCategory === 'updated'
    );
  }

  get viewLinkVisible(): boolean {
    return this.isComplete || (this.completedTests ?? 0) > 0;
  }

  get statusPillCategory(): 'complete' | 'in-progress' | 'updated' {
    if (!this.isComplete) {
      return 'in-progress';
    }
    if (this.dashboardCategory === 'updated') {
      return 'updated';
    }
    return 'complete';
  }

  private get itemStatusList(): OrderedItemStatus[] {
    return this._itemStatusList;
  }
}
